/**
 * @pj/engine/tilemap — paints a whole MapData onto one canvas-backed texture.
 *
 * One draw surface (instead of a sprite per tile) makes neighbor-aware art
 * trivial: shoreline foam, path rims and pond corners look hand-placed. Water
 * and flowers re-paint their cells each animation frame and the texture source
 * re-uploads — the Gen-3 8-frame water cadence (~267ms/frame) for pennies.
 */
import { Texture } from "pixi.js";
import { PALETTE, tileIndexAt, type MapData } from "@pj/content";
import { PAL, TILE, makeCanvas, noise, px, rect } from "./art";

const WATER_FRAMES = 4;

type Cell = { tx: number; ty: number; name: string };

export class MapPainter {
  readonly texture: Texture;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly map: MapData;
  private readonly animated: Cell[] = [];

  constructor(map: MapData) {
    this.map = map;
    const [canvas, ctx] = makeCanvas(map.width * TILE, map.height * TILE);
    this.ctx = ctx;
    this.texture = Texture.from(canvas);
    this.paintAll(0);
  }

  destroy(): void {
    this.texture.destroy(true);
  }

  private nameAt(tx: number, ty: number): string {
    const idx = tileIndexAt(this.map, tx, ty);
    if (idx === null) return "tree"; // out of bounds reads as forest
    return PALETTE[idx]?.name ?? "lawn";
  }

  private isWaterAt(tx: number, ty: number): boolean {
    return this.nameAt(tx, ty) === "water";
  }

  paintAll(frame: number): void {
    this.animated.length = 0;
    for (let ty = 0; ty < this.map.height; ty++) {
      for (let tx = 0; tx < this.map.width; tx++) {
        const name = this.nameAt(tx, ty);
        this.paintCell(tx, ty, name, frame);
        if (name === "water" || name === "flowers") this.animated.push({ tx, ty, name });
      }
    }
    this.texture.source.update();
  }

  /** Re-paint only animated cells (water swell, flower wiggle). */
  animate(frame: number): void {
    for (const c of this.animated) this.paintCell(c.tx, c.ty, c.name, frame);
    this.texture.source.update();
  }

  private paintCell(tx: number, ty: number, name: string, frame: number): void {
    const ctx = this.ctx;
    const x = tx * TILE;
    const y = ty * TILE;
    switch (name) {
      case "lawn":
        this.paintLawn(x, y, tx, ty);
        break;
      case "flowers":
        this.paintFlowers(x, y, tx, ty, frame);
        break;
      case "tall_grass":
        this.paintTallGrass(x, y, tx, ty);
        break;
      case "path":
      case "gate_slot": // gates sit on a path stub; the gate entity overlays
        this.paintPath(x, y, tx, ty);
        break;
      case "sand":
        this.paintSand(x, y, tx, ty);
        break;
      case "water":
        this.paintWater(x, y, tx, ty, frame);
        break;
      case "tree":
        this.paintTree(x, y, tx, ty);
        break;
      case "wall":
      case "door_slot":
        this.paintWall(x, y);
        break;
      case "window":
        this.paintWindow(x, y);
        break;
      case "roof_ridge":
        this.paintRoof(x, y, tx, "ridge");
        break;
      case "roof_mid":
        this.paintRoof(x, y, tx, "mid");
        break;
      case "roof_eave":
        this.paintRoof(x, y, tx, "eave");
        break;
      case "floor_wood":
        this.paintFloor(x, y, tx, ty);
        break;
      case "wall_interior":
        this.paintInteriorWall(x, y, ty);
        break;
      case "mat":
        this.paintMat(x, y, tx, ty);
        break;
      case "table":
        this.paintTable(x, y, tx, ty);
        break;
      default:
        rect(ctx, x, y, TILE, TILE, PAL.lawn);
    }
  }

  // --- terrain -----------------------------------------------------------------

  private paintLawn(x: number, y: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.lawn);
    // Sparse single-pixel noise — restraint is the Gen-3 look.
    for (let i = 0; i < 6; i++) {
      const nx = Math.floor(noise(tx, ty, i) * TILE);
      const ny = Math.floor(noise(tx, ty, i + 11) * TILE);
      px(ctx, x + nx, y + ny, i % 2 === 0 ? PAL.lawnLight : PAL.lawnDark);
    }
    // Tiny cut-grass tufts on some tiles.
    if (noise(tx, ty, 99) > 0.6) {
      const gx = x + 3 + Math.floor(noise(tx, ty, 7) * 8);
      const gy = y + 9 + Math.floor(noise(tx, ty, 8) * 5);
      px(ctx, gx, gy, PAL.lawnDarkest);
      px(ctx, gx + 2, gy, PAL.lawnDarkest);
      px(ctx, gx + 1, gy + 1, PAL.lawnDark);
    }
  }

  private paintFlowers(x: number, y: number, tx: number, ty: number, frame: number): void {
    this.paintLawn(x, y, tx, ty);
    const ctx = this.ctx;
    const wiggle = frame % 2 === 0 ? 0 : noise(tx, ty, 4) > 0.5 ? 1 : -1;
    for (const [bx, by] of [
      [3 + wiggle, 3],
      [10 - wiggle, 9],
    ]) {
      // leaves
      px(ctx, x + bx! - 1, y + by! + 3, PAL.leafDark);
      px(ctx, x + bx! + 3, y + by! + 3, PAL.leafDark);
      // four cream petals around a red center
      rect(ctx, x + bx! + 1, y + by!, 1, 1, PAL.skinHi);
      rect(ctx, x + bx! + 1, y + by! + 2, 1, 1, PAL.skinHi);
      rect(ctx, x + bx!, y + by! + 1, 1, 1, PAL.skinHi);
      rect(ctx, x + bx! + 2, y + by! + 1, 1, 1, PAL.skinHi);
      px(ctx, x + bx! + 1, y + by! + 1, PAL.roofDark);
    }
  }

  private paintTallGrass(x: number, y: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.leafDark);
    // Four staggered rows of interlocking V-blades.
    for (let r = 0; r < 4; r++) {
      const by = r * 4;
      const off = (r % 2) * 2;
      for (let b = 0; b < 4; b++) {
        const bx = b * 4 + off;
        const X = (n: number) => x + ((bx + n) % TILE);
        px(ctx, X(0), y + by + 1, PAL.leaf);
        px(ctx, X(2), y + by + 1, PAL.leaf);
        px(ctx, X(1), y + by + 2, PAL.leaf);
        px(ctx, X(1), y + by + 3, PAL.leafDarkest);
        if (noise(tx * 4 + b, ty * 4 + r, 3) > 0.7) px(ctx, X(0), y + by, PAL.leafHi);
      }
    }
  }

  private paintPath(x: number, y: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.sand);
    for (let i = 0; i < 4; i++) {
      const nx = Math.floor(noise(tx, ty, i + 20) * TILE);
      const ny = Math.floor(noise(tx, ty, i + 31) * TILE);
      px(ctx, x + nx, y + ny, i % 2 === 0 ? PAL.sandHi : PAL.sandShade);
    }
    // Soft rim where the path meets grass, nibbled so it never reads ruler-straight.
    const edge = (side: "n" | "s" | "w" | "e", other: string) => {
      if (other === "path" || other === "sand") return;
      for (let i = 0; i < TILE; i++) {
        if (noise(tx * TILE + i, ty, side.charCodeAt(0)) > 0.8) continue; // nibble
        if (side === "n") px(ctx, x + i, y, PAL.sandShade);
        if (side === "s") px(ctx, x + i, y + TILE - 1, PAL.sandDark);
        if (side === "w") px(ctx, x, y + i, PAL.sandShade);
        if (side === "e") px(ctx, x + TILE - 1, y + i, PAL.sandDark);
      }
    };
    edge("n", this.nameAt(tx, ty - 1));
    edge("s", this.nameAt(tx, ty + 1));
    edge("w", this.nameAt(tx - 1, ty));
    edge("e", this.nameAt(tx + 1, ty));
  }

  private paintSand(x: number, y: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.sandHi);
    for (let i = 0; i < 5; i++) {
      const nx = Math.floor(noise(tx, ty, i + 40) * TILE);
      const ny = Math.floor(noise(tx, ty, i + 51) * TILE);
      px(ctx, x + nx, y + ny, i % 2 === 0 ? PAL.sand : PAL.sandShade);
    }
  }

  private paintWater(x: number, y: number, tx: number, ty: number, frame: number): void {
    const ctx = this.ctx;
    const f = frame % WATER_FRAMES;
    rect(ctx, x, y, TILE, TILE, PAL.water);
    // Soft horizontal swell bands, broken/offset so they read as swells.
    for (const band of [3, 10]) {
      const by = (band + f) % TILE;
      for (let i = 0; i < TILE; i++) {
        if ((i + tx * 3 + f) % 7 === 0) continue; // break the band
        px(ctx, x + i, y + by, PAL.waterDark);
        if ((i + tx) % 5 === 0) px(ctx, x + i, y + ((by + 1) % TILE), PAL.waterMid);
      }
    }
    // Squiggle chevrons with an 8-step-ish lifecycle, offset per tile.
    const phase = (f + tx + ty * 2) % WATER_FRAMES;
    const sx = 3 + Math.floor(noise(tx, ty, 60) * 7);
    const sy = 4 + Math.floor(noise(tx, ty, 61) * 8);
    if (phase === 0) {
      rect(ctx, x + sx, y + sy, 2, 1, PAL.waterHi);
    } else if (phase === 1) {
      px(ctx, x + sx - 1, y + sy, PAL.waterRipple);
      rect(ctx, x + sx, y + sy + 1, 2, 1, PAL.waterHi);
      px(ctx, x + sx + 2, y + sy, PAL.waterRipple);
    } else if (phase === 2) {
      rect(ctx, x + sx - 1, y + sy + 1, 4, 1, PAL.waterRipple);
      px(ctx, x + sx, y + sy, PAL.glint); // the khaki Gen-3 sparkle
    }
    // Breathing foam rim against land: swells on frames 1-2, recedes after.
    const foamColors =
      f === 1 || f === 2 ? [PAL.foam, PAL.foam3] : ([PAL.foam2, PAL.foam4] as const);
    const lap = (side: "n" | "s" | "w" | "e") => {
      for (let i = 0; i < TILE; i++) {
        const tick = (i + f) % 4 !== 0;
        if (side === "n") {
          px(ctx, x + i, y, foamColors[0]!);
          if (tick) px(ctx, x + i, y + 1, foamColors[1]!);
        }
        if (side === "s" && tick) px(ctx, x + i, y + TILE - 1, foamColors[1]!);
        if (side === "w") {
          px(ctx, x, y + i, foamColors[0]!);
          if (tick) px(ctx, x + 1, y + i, foamColors[1]!);
        }
        if (side === "e" && tick) px(ctx, x + TILE - 1, y + i, foamColors[1]!);
      }
    };
    if (!this.isWaterAt(tx, ty - 1)) lap("n");
    if (!this.isWaterAt(tx, ty + 1)) lap("s");
    if (!this.isWaterAt(tx - 1, ty)) lap("w");
    if (!this.isWaterAt(tx + 1, ty)) lap("e");
  }

  private paintTree(x: number, y: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    // Ground under the tree.
    rect(ctx, x, y, TILE, TILE, PAL.lawn);
    // Trunk with root feet.
    rect(ctx, x + 6, y + 11, 4, 5, PAL.trunk);
    rect(ctx, x + 8, y + 11, 2, 5, PAL.trunkDark);
    px(ctx, x + 5, y + 15, PAL.trunkDark);
    px(ctx, x + 10, y + 15, PAL.trunkDark);
    // Round canopy out of scallop clusters.
    rect(ctx, x + 2, y + 2, 12, 9, PAL.leaf);
    rect(ctx, x + 4, y + 1, 8, 1, PAL.leaf);
    rect(ctx, x + 1, y + 4, 1, 5, PAL.leaf);
    rect(ctx, x + 14, y + 4, 1, 5, PAL.leaf);
    rect(ctx, x + 3, y + 10, 10, 2, PAL.leafDark); // canopy underside
    rect(ctx, x + 5, y + 12, 6, 1, PAL.leafDarkest);
    // Scallop shading + highlights (stable per tree).
    for (let i = 0; i < 5; i++) {
      const nx = 2 + Math.floor(noise(tx, ty, i + 70) * 11);
      const ny = 2 + Math.floor(noise(tx, ty, i + 81) * 7);
      px(ctx, x + nx, y + ny, PAL.leafDark);
      if (i < 3) px(ctx, x + nx - 1, y + ny - 1, PAL.leafHi);
    }
    px(ctx, x + 4, y + 2, PAL.leafHi);
    px(ctx, x + 9, y + 3, PAL.leafHi);
  }

  // --- buildings ------------------------------------------------------------------

  private paintWall(x: number, y: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.plaster);
    rect(ctx, x, y, TILE, 1, PAL.plasterShade); // eave shadow line
    rect(ctx, x, y + 11, TILE, 1, PAL.plasterDark); // wainscot
    rect(ctx, x, y + 12, TILE, 3, PAL.plasterShade);
    rect(ctx, x, y + 15, TILE, 1, PAL.plasterDarkest); // foundation
  }

  private paintWindow(x: number, y: number): void {
    this.paintWall(x, y);
    const ctx = this.ctx;
    rect(ctx, x + 3, y + 2, 10, 9, PAL.woodDark); // frame
    rect(ctx, x + 4, y + 3, 8, 3, PAL.glassHi); //   sky reflection
    rect(ctx, x + 4, y + 6, 8, 4, PAL.glass);
    rect(ctx, x + 7, y + 3, 1, 7, PAL.woodDark); //  mullion
    rect(ctx, x + 3, y + 11, 10, 1, PAL.plaster); // sill highlight
  }

  private paintRoof(x: number, y: number, tx: number, kind: "ridge" | "mid" | "eave"): void {
    const ctx = this.ctx;
    if (kind === "ridge") {
      rect(ctx, x, y, TILE, 1, PAL.roofDark);
      rect(ctx, x, y + 1, TILE, 3, PAL.roofHi);
      rect(ctx, x, y + 4, TILE, 12, PAL.roof);
      rect(ctx, x, y + 7, TILE, 1, PAL.roofShade);
    } else if (kind === "mid") {
      rect(ctx, x, y, TILE, TILE, PAL.roof);
      rect(ctx, x, y + 7, TILE, 1, PAL.roofShade);
      rect(ctx, x, y + 15, TILE, 1, PAL.roofShade);
    } else {
      rect(ctx, x, y, TILE, 8, PAL.roof);
      rect(ctx, x, y + 8, TILE, 5, PAL.roofShade);
      rect(ctx, x, y + 13, TILE, 3, PAL.roofDark); // overhanging eave edge
    }
    // Vertical tile seams every 8px.
    for (let sy = 0; sy < TILE; sy += 2) {
      px(ctx, x + ((tx % 2) * 8 + 7) % TILE, y + sy, PAL.roofShade);
    }
  }

  // --- interiors -------------------------------------------------------------------

  private paintFloor(x: number, y: number, tx: number, ty: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.wood);
    for (let row = 0; row < 4; row++) {
      const ry = y + row * 4;
      rect(ctx, x, ry, TILE, 1, PAL.woodLight);
      rect(ctx, x, ry + 3, TILE, 1, PAL.woodShade);
      const seam = ((ty * 4 + row) % 2) * 8 + 4;
      px(ctx, x + seam, ry + 1, PAL.woodShade);
      px(ctx, x + seam, ry + 2, PAL.woodShade);
    }
    void tx;
  }

  private paintInteriorWall(x: number, y: number, ty: number): void {
    const ctx = this.ctx;
    rect(ctx, x, y, TILE, TILE, PAL.plasterDark);
    rect(ctx, x, y + 4, TILE, 1, PAL.plasterDarkest);
    rect(ctx, x, y + 9, TILE, 1, PAL.plasterDarkest);
    rect(ctx, x, y + 14, TILE, 2, PAL.woodDark);
    if (ty === 0) rect(ctx, x, y, TILE, 3, PAL.plasterDarkest); // ceiling shadow
  }

  private paintMat(x: number, y: number, tx: number, ty: number): void {
    this.paintFloor(x, y, tx, ty);
    const ctx = this.ctx;
    rect(ctx, x + 2, y + 3, 12, 10, PAL.lawnDarkest);
    rect(ctx, x + 3, y + 4, 10, 8, PAL.lawnDark);
    rect(ctx, x + 4, y + 6, 8, 1, PAL.lawnDarkest);
    rect(ctx, x + 4, y + 9, 8, 1, PAL.lawnDarkest);
  }

  private paintTable(x: number, y: number, tx: number, ty: number): void {
    this.paintFloor(x, y, tx, ty);
    const ctx = this.ctx;
    rect(ctx, x + 1, y + 3, 14, 8, PAL.woodDark); //  outline
    rect(ctx, x + 2, y + 4, 12, 6, PAL.woodLight); // top
    rect(ctx, x + 2, y + 4, 12, 1, PAL.woodHi); //    highlight
    rect(ctx, x + 2, y + 11, 2, 3, PAL.woodShade); // legs
    rect(ctx, x + 12, y + 11, 2, 3, PAL.woodShade);
  }
}
